import { getApiBaseUrl } from '../../../../core/config/api';
import type { PanelInterventionDetail } from '../InterventionsCreate';

/** Paramètres pour `submitInterventionScenario` extraits de InterventionsCreate.tsx. */
export interface SubmitInterventionScenarioOpts {
  formData: FormData;
  scenarioKind: 'aucun' | 'tuto' | 'exceptionnelle';
  conditionBatimentRows: { config_building_id: number; quantite_min: number }[];
  vehiculeQuantites: Record<number, number>;
  evolutionTaux: Record<number, number>;
  isEditMode: boolean;
  editingId: number;
  panelEditDetail: PanelInterventionDetail | null;
  conditions: number[];
  narrativeAppel?: string;
  narrativeRevelations?: Array<{ phase: string; texte: string }>;
}

export interface SubmitInterventionScenarioResult {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Construit le payload `cta_intervention` puis le POST/PUT vers l'API.
 * Extrait de InterventionsCreate.tsx pour respecter la limite de 500 lignes.
 */
export async function submitInterventionScenario(
  opts: SubmitInterventionScenarioOpts
): Promise<SubmitInterventionScenarioResult> {
  const {
    formData,
    scenarioKind,
    conditionBatimentRows,
    vehiculeQuantites,
    evolutionTaux,
    isEditMode,
    editingId,
    panelEditDetail,
    conditions,
    narrativeAppel,
    narrativeRevelations,
  } = opts;

  const titre = formData.get('titre');
  const isTuto = scenarioKind === 'tuto';
  const isExceptionnelle = scenarioKind === 'exceptionnelle';
  const batimentsPayload = conditionBatimentRows
    .filter((row) => row.config_building_id > 0 && row.quantite_min >= 1)
    .map((row) => ({
      config_building_id: row.config_building_id,
      quantite_min: row.quantite_min,
    }));
  const totalVehiculesRequis = Object.values(vehiculeQuantites).reduce(
    (sum, qty) => sum + Math.max(0, qty || 0),
    0
  );
  if (totalVehiculesRequis < 1) {
    return {
      success: false,
      error: 'Ajoutez au moins 1 véhicule requis pour débloquer/générer cet appel CTA.',
    };
  }

  const interventionData = {
    types_mission: formData
      .getAll('types_mission')
      .map((v) => Number(v))
      .filter((n) => !Number.isNaN(n)),
    conditions: isEditMode ? conditions : [],
    conditions_spec: {
      batiments: batimentsPayload,
      competence_ids: [],
    },
    statut: panelEditDetail?.statut ?? 'EN_COURS',
    is_tuto: isTuto,
    is_exceptionnelle: isExceptionnelle,
    titres: titre ? [titre.toString()] : [],
    contextes_app: {
      heure_debut: formData.get('heure_debut') || '',
      heure_fin: formData.get('heure_fin') || '',
      jours_semaine: [],
      mois: [],
      meteo: '',
      heure: '',
    },
    params: {
      temps_max: parseInt(formData.get('temps_max') as string) || 15,
      moyens_min: { sp: [], samu: [], fdo: [], autres: [] },
      victimes_min: parseInt(formData.get('victimes_min') as string) || 0,
      victimes_max: parseInt(formData.get('victimes_max') as string) || 0,
      hostiles_min: parseInt(formData.get('hostiles_min') as string) || 0,
      hostiles_max: parseInt(formData.get('hostiles_max') as string) || 0,
      vehicules_accidentes_min:
        parseInt(formData.get('vehicules_accidentes_min') as string) || 0,
      vehicules_accidentes_max:
        parseInt(formData.get('vehicules_accidentes_max') as string) || 0,
    },
    recompense: {
      xp: parseInt(formData.get('recompense_xp') as string) || 0,
      credits: parseInt(formData.get('recompense_credits') as string) || 0,
    },
    facteurs_dynamiques: {
      meteo: '',
      heure: '',
      circulation: '',
      evenement_local: '',
    },
    evolutions: Object.entries(evolutionTaux)
      .filter(([, taux]) => taux > 0)
      .map(([id, taux]) => ({ id: Number(id), taux })),
    vehicules_requis: Object.entries(vehiculeQuantites)
      .filter(([id, q]) => q > 0 && Number(id) > 0)
      .map(([id, q]) => ({ id_vehicule: Number(id), quantite: q })),
    messages_radio: [],
    renforts_possibles: [],
    score_si: { bonus: [], malus: [] },
    resultats: { positif: '', neutre: '', negatif: '' },
    notes_cta: [],
    memo: '',
    narrative_appel: narrativeAppel?.trim() || null,
    narrative_revelations:
      narrativeRevelations && narrativeRevelations.length > 0
        ? narrativeRevelations.filter((r) => r.texte.trim().length > 0)
        : null,
    ...(isEditMode && panelEditDetail?.service_id
      ? { service_id: panelEditDetail.service_id }
      : {}),
  };

  const token = localStorage.getItem('token');
  if (!token) {
    return { success: false, error: "Token d'authentification manquant" };
  }

  const apiUrl = getApiBaseUrl();
  const saveUrl = isEditMode
    ? `${apiUrl}/api/cta/interventions/${editingId}`
    : `${apiUrl}/api/cta/interventions/create`;
  const saveMethod = isEditMode ? 'PUT' : 'POST';

  try {
    const response = await fetch(saveUrl, {
      method: saveMethod,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify(interventionData),
    });

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Réponse non-JSON reçue:', text.substring(0, 200));
      return {
        success: false,
        error: 'Le serveur a retourné une réponse invalide. Vérifiez que la route existe.',
      };
    }

    const data = await response.json();
    if (data.success) {
      return {
        success: true,
        message: isEditMode
          ? 'Scénario mis à jour avec succès.'
          : "Scénario d'intervention créé avec succès. Vous pouvez en créer un autre ci-dessous.",
      };
    }
    return {
      success: false,
      error:
        data.error ||
        (isEditMode ? 'Erreur lors de la mise à jour.' : 'Erreur lors de la création du scénario'),
    };
  } catch (err: any) {
    return {
      success: false,
      error:
        err?.message ||
        (isEditMode ? 'Erreur lors de la mise à jour.' : 'Erreur lors de la création du scénario'),
    };
  }
}
